<template>
    <div class="wrapper">
        <v-card class="account" outlined>
            <v-card-title class="page-heading primary">
                <v-tabs
                    v-model="activeTab"
                    color="white"
                    background-color="transparent"
                    fixed-tabs
                    slider-color="yellow"
                    height="60"
                    dark
                >
                    <v-tab v-if="user && user.role_id === 1">{{ $t('general.account') }}</v-tab>
                    <v-tab>{{ $t('general.company') }}</v-tab>
                    <v-tab>{{ $tc('general.receipt', 2) }}</v-tab>
                    <v-tab>{{ $tc('general.integration', 2) }}</v-tab>
                    <v-tab>{{ $t('general.settings') }}</v-tab>
                </v-tabs>
            </v-card-title>

            <v-card-text class="page-content">
                <v-tabs-items v-model="activeTab">
                    <v-tab-item v-if="user && user.role_id === 1">
                        <v-form ref="accountForm">
                            <v-text-field
                                v-model="accountForm.name"
                                :label="$t('general.name')"
                                :rules="accountForm.rules.name"
                                validate-on-blur
                                autocomplete="off"
                            />

                            <v-text-field
                                v-model="accountForm.email"
                                :label="$t('general.email')"
                                hide-details
                                class="mb-5"
                                readonly
                            />

                            <v-text-field
                                v-if="locale === 'me'"
                                v-model="accountForm.operator_code"
                                v-mask="'AA###AA###'"
                                :label="$t('general.operator_code')"
                                :hint="$t('general.operator_code_description')"
                                :rules="accountForm.rules.operator_code"
                                :error-messages="accountForm.errors.operator_code"
                                validate-on-blur
                                autocomplete="off"
                                @input="delete accountForm.errors.operator_code"
                            />

                            <div class="d-flex flex-wrap mt-6">
                                <v-btn
                                    color="primary"
                                    text
                                    @click="openPinForm()"
                                >
                                    {{ $t('account.change_pin') }}
                                </v-btn>

                                <v-btn
                                    color="primary"
                                    text
                                    @click="openPasswordForm()"
                                >
                                    {{ $t('account.change_password') }}
                                </v-btn>

                                <v-btn
                                    v-if="locale === 'me'"
                                    color="primary"
                                    text
                                    @click="openCertificateForm()"
                                >
                                    {{ $t('account.certificate_set') }}
                                </v-btn>
                            </div>

                            <v-btn
                                class="save-btn"
                                color="primary"
                                depressed
                                :loading="accountForm.busy"
                                :disabled="accountForm.busy"
                                @click="saveAccount()"
                            >
                                {{ $t('general.save') }}
                            </v-btn>
                        </v-form>
                    </v-tab-item>

                    <v-tab-item>
                        <v-form ref="venueForm">
                            <v-text-field
                                v-model="venueForm.name"
                                :label="$t('general.venue')"
                                :placeholder="$t('general.venue_placeholder')"
                                :rules="venueForm.rules.name"
                                :error-messages="venueForm.errors.name"
                                validate-on-blur
                                autocomplete="off"
                            />

                            <v-text-field
                                v-if="locale === 'me'"
                                v-model="venueForm.hash"
                                v-mask="'AA###AA###'"
                                :label="$t('general.venue_code')"
                                :rules="venueForm.rules.hash"
                                :error-messages="venueForm.errors.hash"
                                validate-on-blur
                                readonly
                            />

                            <v-text-field
                                v-model="venueForm.address"
                                :label="$t('general.venue_address')"
                                :placeholder="$t('general.venue_address_placeholder')"
                                :rules="venueForm.rules.address"
                                :error-messages="venueForm.errors.address"
                                validate-on-blur
                                autocomplete="off"
                            />

                            <div class="section">
                                <div class="d-flex align-center mt-1 h40">
                                    <div class="primary--text">
                                        {{ $t('general.company_info') }}
                                    </div>

                                    <v-spacer />

                                    <template v-if="locale === 'sr'">
                                        <v-btn
                                            v-if="!einvoiceEnabled"
                                            color="primary"
                                            outlined
                                            @click="openEinvoicesForm"
                                        >
                                            {{ $t('einvoice.activate') }}
                                        </v-btn>

                                        <v-alert
                                            v-else
                                            class="mb-0"
                                            type="success"
                                            text
                                            dense
                                        >
                                            {{ $t('einvoice.activated') }}
                                        </v-alert>
                                    </template>
                                </div>

                                <v-text-field
                                    v-model="venueForm.company_name"
                                    :label="$t('general.company_name')"
                                    :placeholder="$t('general.company_name_placeholder')"
                                    :rules="venueForm.rules.company_name"
                                    :error-messages="venueForm.errors.company_name"
                                    validate-on-blur
                                />

                                <v-text-field
                                    v-model="venueForm.company_address"
                                    :label="$t('general.company_address')"
                                    :placeholder="$t('general.company_address_placeholder')"
                                    :rules="venueForm.rules.company_address"
                                    :error-messages="venueForm.errors.company_address"
                                    validate-on-blur
                                />

                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="venueForm.postal_number"
                                            :label="$t('general.postal_number')"
                                            :placeholder="$t('general.postal_number_placeholder')"
                                            :rules="venueForm.rules.postal_number"
                                            :error-messages="venueForm.errors.postal_number"
                                            validate-on-blur
                                            autocomplete="off"
                                        />
                                    </v-col>

                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="venueForm.city"
                                            :label="$t('general.city')"
                                            :placeholder="$t('general.city_placeholder')"
                                            :rules="venueForm.rules.city"
                                            :error-messages="venueForm.errors.city"
                                            validate-on-blur
                                            autocomplete="off"
                                        />
                                    </v-col>
                                </v-row>

                                <v-row class="mt-0">
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="venueForm.tax_id_number"
                                            :label="$t('general.tax_id_number')"
                                            :rules="venueForm.rules.tax_id_number"
                                            :error-messages="venueForm.errors.tax_id_number"
                                            validate-on-blur
                                        />
                                    </v-col>

                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="venueForm.registration_number"
                                            :label="$t('general.registration_number')"
                                            :rules="venueForm.rules.registration_number"
                                            :error-messages="venueForm.errors.registration_number"
                                            validate-on-blur
                                            autocomplete="off"
                                        />
                                    </v-col>
                                </v-row>

                                <v-text-field
                                    v-model="venueForm.bank_account_number"
                                    :label="$t('general.bank_account_number')"
                                    :rules="venueForm.rules.bank_account_number"
                                    :error-messages="venueForm.errors.bank_account_number"
                                    validate-on-blur
                                    autocomplete="off"
                                />
                            </div>

                            <v-btn
                                class="save-btn"
                                color="primary"
                                depressed
                                :loading="venueForm.busy"
                                :disabled="venueForm.busy"
                                @click="saveVenue()"
                            >
                                {{ $t('general.save') }}
                            </v-btn>
                        </v-form>
                    </v-tab-item>

                    <v-tab-item>
                        <v-form ref="invoiceForm">
                            <div class="text-subtitle-1 mb-3">{{ $t('account.invoice_header') }}</div>

                            <html-editor v-model="invoiceForm.invoice_header" />

                            <div class="text-subtitle-1 mt-6 mb-3">{{ $t('account.invoice_note') }}</div>

                            <v-textarea
                                v-model="invoiceForm.invoice_note"
                                outlined
                                rows="2"
                            />

                            <template v-if="locale !== 'ba'">
                                <div class="text-subtitle-1 mb-3">{{ $t('account.receipt_header') }}</div>

                                <v-textarea
                                    v-model="invoiceForm.receipt_note2"
                                    outlined
                                    rows="2"
                                    @keydown="preventPipe"
                                />
                            </template>

                            <div class="text-subtitle-1 mb-3">{{ $t('account.receipt_note') }}</div>

                            <v-textarea
                                v-model="invoiceForm.receipt_note1"
                                outlined
                                rows="2"
                                @keydown="preventPipe"
                            />

                            <v-btn
                                class="save-btn"
                                color="primary"
                                depressed
                                :loading="invoiceForm.busy"
                                :disabled="invoiceForm.busy"
                                @click="saveInvoice()"
                            >
                                {{ $t('general.save') }}
                            </v-btn>
                        </v-form>
                    </v-tab-item>

                    <v-tab-item>
                        <div class="section mt-0">
                            <div class="primary--text">
                                {{ $t('integrations.payoneos.heading') }}
                            </div>

                            <v-text-field
                                v-model="integrationsForm.payoneos_user_hash"
                                :label="$t('integrations.payoneos.user_hash')"
                                :error-messages="integrationsForm.$errors.payoneos_user_hash"
                                :append-icon="showPayoneosHash ? 'fal fa-eye' : 'fal fa-eye-slash'"
                                :type="showPayoneosHash ? 'text' : 'password'"
                                clear-icon="fal fa-times"
                                clearable
                                @click:append="showPayoneosHash = !showPayoneosHash"
                                @blur="integrationsForm.validate('payoneos_user_hash')"
                                @input="delete integrationsForm.$errors.payoneos_user_hash"
                            />
                        </div>

                        <v-btn
                            class="save-btn"
                            color="primary"
                            depressed
                            :loading="integrationsForm.$busy"
                            :disabled="integrationsForm.$busy"
                            @click="saveIntegrations()"
                        >
                            {{ $t('general.save') }}
                        </v-btn>
                    </v-tab-item>

                    <v-tab-item>
                        <v-form ref="settingsForm">
                            <v-switch
                                v-model="settingsForm.cash_change_enabled"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.cash_change_enabled')"
                                inset
                            />

                            <v-divider />

                            <v-switch
                                v-model="settingsForm.user_deposit_enabled"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.user_deposit_enabled')"
                                inset
                            />

                            <v-divider />

                            <v-switch
                                v-model="settingsForm.show_count_in_product_search"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.show_count_in_product_search')"
                                inset
                            />

                            <v-divider />

                            <v-switch
                                v-model="settingsForm.item_comments_on_fiscal_receipts"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.item_comments_on_fiscal_receipts')"
                                inset
                            />

                            <v-divider v-if="locale === 'sr'" />

                            <v-switch
                                v-if="locale === 'sr'"
                                v-model="settingsForm.cyrilic_receipt"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.cyrilic_receipt')"
                                inset
                            />

                            <v-divider />

                            <v-switch
                                v-model="settingsForm.user_logs_enabled"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.user_logs_enabled')"
                                inset
                            />

                            <v-divider />

                            <v-switch
                                v-model="settingsForm.hide_product_quantities"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.hide_product_quantities')"
                                inset
                            />

                            <v-divider v-if="locale === 'sr'" />

                            <v-switch
                                v-if="locale === 'sr'"
                                v-model="settingsForm.wholesale"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.wholesale')"
                                inset
                            />

                            <v-divider v-if="locale === 'me' || locale === 'sr'" />

                            <v-switch
                                v-if="locale === 'me' || locale === 'sr'"
                                v-model="settingsForm.language_en"
                                class="ma-4"
                                hide-details
                                :label="$t('venue_settings.language_en')"
                                inset
                            />

                            <v-btn
                                class="save-btn"
                                color="primary"
                                depressed
                                :loading="settingsForm.busy"
                                :disabled="settingsForm.busy"
                                @click="saveSettings()"
                            >
                                {{ $t('general.save') }}
                            </v-btn>
                        </v-form>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
        </v-card>

        <cod-dialog id="changePassword" :show.sync="passwordFormVisible">
            <template slot="header">
                <span>{{ $t('account.change_password') }}</span>
            </template>

            <div class="pa-5" slot="content">
                <v-form ref="passwordForm">
                    <v-text-field
                        v-model="passwordForm.old_password"
                        :label="$t('account.old_password')"
                        autofocus
                        :rules="passwordForm.rules.old_password"
                        counter
                        :error-messages="passwordForm.errors.old_password"
                        :append-icon="showOldPassword ? 'fal fa-eye' : 'fal fa-eye-slash'"
                        :type="showOldPassword ? 'text' : 'password'"
                        autocomplete="off"
                        @click:append="showOldPassword = !showOldPassword"
                    />

                    <v-text-field
                        v-model="passwordForm.new_password"
                        :label="$t('account.new_password')"
                        :rules="passwordForm.rules.new_password"
                        :append-icon="showNewPassword ? 'fal fa-eye' : 'fal fa-eye-slash'"
                        :type="showNewPassword ? 'text' : 'password'"
                        counter
                        autocomplete="off"
                        @click:append="showNewPassword = !showNewPassword"
                    />

                    <v-text-field
                        v-model="passwordForm.new_password_confirmation"
                        :label="$t('account.new_password_confirmation')"
                        :rules="passwordForm.rules.new_password_confirmation"
                        :type="showNewPassword ? 'text' : 'password'"
                        autocomplete="off"
                    />
                </v-form>
            </div>

            <template slot="footer" class="flex">
                <v-btn
                    class="minw100 flex-1 mr-1"
                    color="primary"
                    outlined
                    @click="passwordFormVisible = false"
                >
                    {{ $t('general.cancel') }}
                </v-btn>
                <v-btn
                    class="flex-1 ml-1"
                    color="primary"
                    depressed
                    :loading="passwordForm.busy"
                    :disabled="passwordForm.busy"
                    @click="changePassword()"
                >
                    {{ $t('account.change_password') }}
                </v-btn>
            </template>
        </cod-dialog>

        <cod-dialog id="changePin" :show.sync="pinFormVisible">
            <template slot="header">
                <span>{{ $t('account.change_pin') }}</span>
            </template>

            <div class="pa-5" slot="content">
                <v-form ref="pinForm">
                    <v-text-field
                        v-model="pinForm.old_pin"
                        :label="$t('account.old_pin')"
                        :rules="pinForm.rules.old_pin"
                        :error-messages="pinForm.errors.old_pin"
                        counter
                        :type="showOldPin ? 'text' : 'password'"
                        :append-icon="showOldPin ? 'fal fa-eye' : 'fal fa-eye-slash'"
                    />

                    <v-text-field
                        v-model="pinForm.new_pin"
                        :label="$t('account.new_pin')"
                        :rules="pinForm.rules.new_pin"
                        :error-messages="pinForm.errors.new_pin"
                        :append-icon="showNewPin ? 'fal fa-eye' : 'fal fa-eye-slash'"
                        :type="showNewPin ? 'text' : 'password'"
                        counter
                        @click:append="showNewPin = !showNewPin"
                    />

                    <v-text-field
                        v-model="pinForm.new_pin_confirmation"
                        :label="$t('account.new_pin_confirmation')"
                        :rules="pinForm.rules.new_pin_confirmation"
                        :type="showNewPin ? 'text' : 'password'"
                    />
                </v-form>
            </div>

            <template slot="footer" class="flex">
                <v-btn
                    class="minw100 mr-1 flex-1"
                    color="primary"
                    outlined
                    @click="pinFormVisible = false"
                >
                    {{ $t('general.cancel') }}
                </v-btn>
                <v-btn
                    class="minw100 ml-1 flex-1"
                    color="primary"
                    depressed
                    :loading="pinForm.busy"
                    :disabled="pinForm.busy"
                    @click="changePin()"
                >
                    {{ $t('account.change_pin') }}
                </v-btn>
            </template>
        </cod-dialog>

        <cod-dialog id="enableEinvoices" :show.sync="einvoicesFormVisible">
            <template slot="header">
                <span>{{ $t('einvoice.activate') }}</span>
            </template>

            <div class="pa-5" slot="content">
                <template v-if="!einvoiceAllowed">
                    <v-alert
                        v-if="!modules.einvoices"
                        class="mb-0"
                        type="warning"
                        prominent
                        text
                    >
                        {{ $t('einvoice.module_requirements') }}
                    </v-alert>

                    <v-alert
                        v-else
                        class="mb-0"
                        type="warning"
                        prominent
                        text
                    >
                        {{ $t('einvoice.activate_requirements') }}
                    </v-alert>
                </template>

                <div v-else>
                    <v-alert
                        class="mb-4"
                        type="warning"
                        prominent
                        text
                    >
                        {{ $t('einvoice.activate_warning') }}
                    </v-alert>

                    <v-text-field
                        v-model="einvoicesForm.api_key"
                        :label="$t('einvoice.api_key')"
                        :hint="$t('einvoice.api_key_description')"
                        persistent-hint
                        :error-messages="einvoicesForm.$errors.api_key"
                        @blur="einvoicesForm.validate('api_key')"
                        @input="delete einvoicesForm.$errors.api_key"
                    />
                </div>
            </div>

            <template v-if="einvoiceAllowed" slot="footer" class="flex">
                <v-btn
                    class="minw100 flex-1 mr-1"
                    color="primary"
                    outlined
                    @click="einvoicesFormVisible = false"
                >
                    {{ $t('general.back') }}
                </v-btn>
                <v-btn
                    class="flex-1 ml-1"
                    color="primary"
                    depressed
                    :loading="einvoicesForm.$busy"
                    :disabled="einvoicesForm.$busy"
                    @click="enableEinvoices()"
                >
                    {{ $t('general.activate') }}
                </v-btn>
            </template>
        </cod-dialog>

        <!-- Certificate upload form -->
        <cod-dialog id="updateCertificate" :show.sync="certificateFormVisible">
            <template slot="header">
                <span>{{ $t('account.certificate_set') }}</span>
            </template>

            <div class="pa-5" slot="content">
                <v-form ref="certificateForm">
                    <v-file-input
                        :label="$t('general.certificate')"
                        :placeholder="$t('general.certificate')"
                        :error-messages="certificateForm.errors.cert"
                        :rules="certificateForm.rules.cert"
                        prepend-icon=""
                        append-icon="fal fa-key"
                        clearable
                        clear-icon="fal fa-times"
                        validate-on-blur
                        @change="certificateForm.encodeFile('cert', $event)"
                    />

                    <v-text-field
                        v-model="certificateForm.cert_password"
                        :type="showCertPassword ? 'text' : 'password'"
                        :label="$t('general.certificate_password')"
                        :placeholder="$t('general.certificate_password')"
                        :error-messages="certificateForm.errors.cert_password"
                        :rules="certificateForm.rules.cert_password"
                        autocomplete="new-password"
                        :append-icon="showCertPassword ? 'fal fa-eye' : 'fal fa-eye-slash'"
                        @click:append="showCertPassword = !showCertPassword"
                    />
                </v-form>
            </div>

            <template slot="footer" class="flex">
                <v-btn
                    class="minw100 flex-1 mr-1"
                    color="primary"
                    outlined
                    @click="certificateFormVisible = false"
                >
                    {{ $t('general.cancel') }}
                </v-btn>
                <v-btn
                    class="flex-1 ml-1"
                    color="primary"
                    depressed
                    :loading="passwordForm.busy"
                    :disabled="passwordForm.busy"
                    @click="uploadCertificate()"
                >
                    {{ $t('account.certificate_save') }}
                </v-btn>
            </template>
        </cod-dialog>
    </div>
</template>

<script>
import * as mutationTypes from '@/store/mutationTypes';
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';
import EinvoiceService from '@/services/EinvoiceService';
import User from '@/models/User';
import Locale from '@/wrappers/Locale';
import HtmlEditor from '@/views/shared/admin/account/HtmlEditor';
import { required } from 'vuelidate/lib/validators';
import TenantService from '@/services/TenantService'
import { mapGetters } from 'vuex';

export default {
    components: {
        CodDialog,
        HtmlEditor,
    },

    created() {
        loadData('/admin/load/account').then((data) => this.onDataLoaded(data));
    },

    data() {
        return {
            activeTab: null,

            accountForm: this.createAccountForm(),
            venueForm: this.createVenueForm(),
            invoiceForm: this.createInvoiceForm(),

            integrationsForm: this.createIntegrationsForm(),
            showPayoneosHash: false,

            settingsForm: this.createSettingsForm(),

            passwordForm: this.createPasswordForm(),
            passwordFormVisible: false,
            showOldPassword: false,
            showNewPassword: false,

            pinForm: this.createPinForm(),
            pinFormVisible: false,
            showOldPin: false,
            showNewPin: false,

            certificateForm: this.createCertificateForm(),
            certificateFormVisible: false,
            showCertPassword: false,

            einvoicesFormVisible: false,
            einvoicesForm: this.createEinvoicesForm(),
        };
    },

    validations: {
        einvoicesForm: {
            api_key: { required },
        }
    },

    computed: {
        ...mapGetters(['modules']),

        einvoiceEnabled() {
            return EinvoiceService.isEnabled();
        },

        einvoiceAllowed() {
            return EinvoiceService.isAllowed();
        },
    },

    methods: {
        /**
         * Create forms
         */
        onDataLoaded(data) {
            this.accountForm = this.createAccountForm(data.account);
            this.venueForm = this.createVenueForm(data.venue);
            this.invoiceForm = this.createInvoiceForm(data.venue);
            this.integrationsForm = this.createIntegrationsForm(data.venue);
            this.settingsForm = this.createSettingsForm(data.venue);
        },

        /**
         * Create account form
         */
        createAccountForm(account = null) {
            return new CodForm({
                name: account ? account.name : (this.tenant ? this.tenant.name : null),
                email: account ? account.email : (this.tenant ? this.tenant.email : null),
                operator_code: account ? account.operator_code : null,

                rules: {
                    name: [ CodForm.rule('required') ],
                    email: [ CodForm.rule('required') ],
                    operator_code: [ CodForm.rule('required'), CodForm.rule('min_length', 10) ],
                }
            });
        },

        /**
         * Create venue form
         */
        createVenueForm(venue = null) {
            return new CodForm({
                name: venue ? venue.name : null,
                hash: venue ? venue.hash : null,
                address: venue ? venue.address : null,
                postal_number: venue ? venue.postal_number : null,
                city: venue ? venue.city : null,
                company_name: venue ? venue.company_name : null,
                company_address: venue ? venue.company_address : null,
                registration_number: venue ? venue.registration_number : null,
                bank_account_number: venue ? venue.bank_account_number : null,
                tax_id_number: venue ? venue.tax_id_number : null,

                rules: {
                    name: [ CodForm.rule('required') ],
                    address: [ CodForm.rule('required') ],
                    hash: [ CodForm.rule('required'), CodForm.rule('min_length', 10) ],
                    tax_id_number: [CodForm.rule('required_if', () => ['me', 'sr', 'ba'].includes(locale))],
                }
            });
        },

        /**
         * Create invoice form
         */
        createInvoiceForm(venue = null) {
            return new CodForm({
                invoice_header: venue?.invoice_header || null,
                invoice_note: venue?.invoice_note || null,
                receipt_note: venue?.receipt_note || null,
                receipt_note1: venue?.receipt_note?.split('|')[0] || null,
                receipt_note2: venue?.receipt_note?.split('|')[1] || null,
            });
        },

        /**
         * Create integrations form
         */
        createIntegrationsForm(venue = null) {
            return new Form(this, 'integrationsForm', {
                payoneos_user_hash: venue?.settings?.integrations?.payoneos?.user_hash || null
            })
        },

        /**
         * Create settings form
         */
        createSettingsForm(venue = null) {
            return new CodForm({
                cash_change_enabled: venue ? venue.settings.cash_change_enabled : false,
                user_deposit_enabled: venue ? venue.settings.user_deposit_enabled : false,
                cyrilic_receipt: venue ? venue.settings.cyrilic_receipt : false,
                show_count_in_product_search: venue ? venue.settings.show_count_in_product_search : false,
                item_comments_on_fiscal_receipts: venue ? venue.settings.item_comments_on_fiscal_receipts : false,
                user_logs_enabled: venue ? venue.settings.user_logs_enabled : false,
                hide_product_quantities: venue ? venue.settings.hide_product_quantities : false,
                wholesale: venue ? venue.wholesale : false,
                language_en: venue ? venue.language === 'en' : false,
            });
        },

        /**
         * Create password form
         */
        createPasswordForm() {
            return new CodForm({
                old_password: null,
                new_password: null,
                new_password_confirmation: null,

                rules: {
                    old_password: [ CodForm.rule('required') ],
                    new_password: [
                        CodForm.rule('required'),
                        CodForm.rule('password'),
                        CodForm.rule('diff', () => this.passwordForm.old_password, this.$t('account.diff_password'))
                    ],
                    new_password_confirmation: [
                        CodForm.rule('required'),
                        CodForm.rule('password'),
                        CodForm.rule('match', () => this.passwordForm.new_password)
                    ],
                }
            });
        },

        /**
         * Create pin form
         */
        createPinForm() {
            return new CodForm({
                old_pin: null,
                new_pin: null,
                new_pin_confirmation: null,

                rules: {
                    old_pin: [ CodForm.rule('required') ],
                    new_pin: [
                        CodForm.rule('required'),
                        CodForm.rule('pin'),
                        CodForm.rule('diff', () => this.pinForm.old_pin, this.$t('account.diff_pin'))
                    ],
                    new_pin_confirmation: [
                        CodForm.rule('required'),
                        CodForm.rule('pin'),
                        CodForm.rule('match', () => this.pinForm.new_pin)
                    ],
                }
            });
        },

        /**
         * Create certificate form
         */
        createCertificateForm() {
            return new CodForm({
                cert: null,
                cert_password: null,
                rules: {
                    cert: [CodForm.rule('required')],
                    cert_password: [CodForm.rule('required')],
                },
            });
        },

        /**
         * Create einvoices form
         */
        createEinvoicesForm() {
            return new Form(this, 'einvoicesForm', {
                api_key: null,
            });
        },

        /**
         * Open password form
         */
        openPasswordForm() {
            this.passwordForm = this.createPasswordForm();
            this.passwordFormVisible = true;
        },

        /**
         * Open certificate form
         */
        openCertificateForm() {
            this.certificateForm = this.createCertificateForm();
            this.certificateFormVisible = true;
        },

        /**
         * Change password
         */
        changePassword() {
            if(!this.$refs.passwordForm.validate()) {
                return;
            }

            ApiService.patch('/admin/account/password', this.passwordForm).then(response => {
                this.passwordFormVisible = false;
                this.$snackbar(this.$t('account.password_changed'), 'success');
            });
        },

        /**
         * Open pin form
         */
        openPinForm() {
            this.pinForm = this.createPinForm();
            this.pinFormVisible = true;
        },

        /**
         * Upload certificate
         */
        async uploadCertificate() {
            if (!this.$refs.certificateForm.validate()) {
                return;
            }
            try {
                let result = await this.certificateForm.patch('/admin/account/certificate', this.certificateForm);
                if (result.data.success) {
                    TenantService.setTenant(result.data.tenant);
                    TenantService.setPkey(result.data.pkey)
                    this.certificateFormVisible = false;
                    this.$snackbar(this.$t('account.certificate_changed'), 'success');
                }
            } catch (e) {
                if (e?.response?.data?.errors?.cert) {
                    this.certificateForm.errors.cert = this.$t('account.certificate_not_valid');
                }
            }
        },

        /**
         * Open einvoices form
         */
        openEinvoicesForm() {
            this.einvoicesForm = this.createEinvoicesForm();
            this.einvoicesFormVisible = true;
        },

        /**
         * Change pin
         */
        changePin() {
            if(!this.$refs.pinForm.validate()) {
                return;
            }

            ApiService.patch('admin/account/pin', this.pinForm).then(response => {
                this.pinFormVisible = false;
                this.$snackbar(this.$t('account.pin_changed'), 'success');
            });
        },

        /**
         * Save account
         */
        saveAccount() {
            if(!this.$refs.accountForm.validate()) {
                return;
            }

            ApiService.patch('/admin/account', this.accountForm).then(response => {
                this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });
                this.$store.commit(mutationTypes.SET_USER, { user: response.data.user });

                User.update(response.data.user);

                this.$snackbar(this.$t('account.name_changed'), 'success');
            });
        },

        /**
         * Save venue
         */
        saveVenue() {
            if(!this.$refs.venueForm.validate()) {
                return;
            }

            ApiService.patch('/admin/account/venue', this.venueForm).then(response => {
                this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });
                this.$snackbar(this.$t('account.venue_information_changed'), 'success');
            });
        },

        /**
         * Save invoice
         */
        async saveInvoice() {
            if(!this.$refs.invoiceForm.validate()) {
                return;
            }

            this.invoiceForm.receipt_note = (this.invoiceForm.receipt_note1 || '') +
                (this.invoiceForm.receipt_note2 ? `|${this.invoiceForm.receipt_note2}` : '')

            ApiService.patch('/admin/account/venue', this.invoiceForm).then(response => {
                this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });
                this.$snackbar(this.$t('account.settings_changed'), 'success');
            });
        },

        /**
         * Save Integrations
         */
         async saveIntegrations() {
            if(!this.integrationsForm.validate()) return;

            ApiService.patch('/admin/account/venue/integrations', this.integrationsForm).then(response => {
                this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });
                this.$snackbar(this.$t('account.integrations_updated'), 'success');
            });
        },

        /**
         * Save settings
         */
        async saveSettings() {
            if(!this.$refs.settingsForm.validate()) {
                return;
            }

            ApiService.patch('/admin/account/venue/settings', this.settingsForm).then(response => {
                this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });
                Locale.setLanguage(response.data.tenant.venue.language);
                this.$snackbar(this.$t('account.settings_changed'), 'success');
            });
        },

        /**
         * Enable Einvoices
         */
        async enableEinvoices() {
            if(!this.einvoicesForm.validate()) return;

            const response = await ApiService.post('/einvoice/enable', this.einvoicesForm);
            if(!response.data.success) {
                this.$set(this.einvoicesForm.$errors, 'api_key', [this.$t(`einvoice.error.${response.data.error}`)]);
                return;
            }

            this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });

            this.$snackbar(this.$t('einvoice.activated'), 'success');
            this.einvoicesFormVisible = false;
        },

        /**
         * Prevent pipe key from being typed
         */
        preventPipe(event) {
            if (event.key === '|') {
                event.preventDefault();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.account {
    max-width: 900px;

    .page-heading {
        padding: 0;
        color: white;
        font-size: 16px;
        word-break: break-word;

        ::v-deep .v-tab {
            white-space: nowrap;
            padding: 0 8px;
            width: fit-content;
        }

        ::v-deep .v-slide-group__prev {
            display: none !important;
        }
    }

    .page-content {
        padding: 0px;

        .v-window-item {
            position: relative;
            min-height: 395px;
            padding: 20px 20px 66px 20px;

            .section {
                padding: 8px 16px 8px 16px;
                background: #f5f6f9;
                margin-bottom: 20px;
                margin-top: 16px;
                border-radius: 4px;
            }

            .save-btn {
                position: absolute;
                bottom: 20px;
                left: 20px;
                right: 20px;
            }
        }
    }
}
</style>
